import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Amend mode for the native document "New" screens.
//
// Every one of these screens (adjustment, transfer, build, fulfillment,
// receipt) already knows how to compose and post its own document. Amending is
// the same screen with three differences, which is all this hook adds:
//
//   1. it is opened with ?amendOf=<id>, and the screen prefills itself from
//      that document (each screen does its own prefill — the shapes have
//      nothing in common)
//   2. a reason is REQUIRED, because an amend rewrites posted books and the
//      "why" is the only part a reader cannot reconstruct from the documents
//   3. it submits to <api>/<id>/amend instead of <api>
//
// The banner is deliberately blunt about what happens on submit. An operator
// who arrives here from the list has already seen one confirm dialog; the
// consequence is restated at the point of no return because the two are far
// apart in time once they start editing lines.

export function useAmendMode() {
  const [searchParams] = useSearchParams();
  const amendOf = searchParams.get('amendOf');
  const [amendReason, setAmendReason] = useState('');
  return {
    amendOf,
    isAmend: !!amendOf,
    amendReason,
    setAmendReason,
    // The server requires 4+ characters; matching it here turns a 400 into a
    // disabled button.
    amendReasonValid: amendReason.trim().length >= 4,
    /** POST target for this screen's submit. */
    postUrl: (api) => (amendOf ? `${api}/${encodeURIComponent(amendOf)}/amend` : api),
    /** Extra body fields the amend endpoint needs. */
    bodyExtras: () => (amendOf ? { amendReason: amendReason.trim() } : {}),
  };
}

/**
 * The amend banner + required reason field. Renders nothing outside amend mode,
 * so a screen can drop it in unconditionally.
 */
export function AmendBanner({ amend, docLabel, what }) {
  if (!amend.isAmend) return null;
  return (
    <div className="nia-card" style={{ borderColor: '#8B6F4E' }}>
      <h3 style={{ marginTop: 0 }}>Amending {docLabel || 'this document'}</h3>
      <p className="nia-hint" style={{ marginTop: 0 }}>
        This does not edit {what || 'the document'}. Posting reverses the original in full — quantity,
        costing and GL — and posts your corrected version under the <strong>same number</strong> as the
        next version. Both the reversal and the correction stay in the general ledger; nothing is
        rewritten in place.
      </p>
      <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
        Reason for the amendment (required — recorded on the new version)
        <input
          className="nia-input"
          value={amend.amendReason}
          onChange={(e) => amend.setAmendReason(e.target.value)}
          placeholder="e.g. counted 12 cases, not 21 — transposed on the sheet"
          maxLength={500}
        />
      </label>
      {!amend.amendReasonValid && amend.amendReason.length > 0 && (
        <p className="nia-hint" style={{ color: '#a33' }}>Give a few more words — this is the audit trail.</p>
      )}
    </div>
  );
}
